import React, { useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  CellLeftV8,
  CellYesNoV8,
  CellYesNoEngV8,
  EditCellV8,
  TableCellV8,
  TableDataState,
  TablePaginatedV8,
  order2Sorting,
  promiseModalDialog,
  toastFailure,
  toastSuccess,
} from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import {
  firstDateUserColumn,
  gridRowSelectedStyle,
  lastDateUserColumn,
} from 'shared-pages/common/grid-helpfunc';
import { useMutation } from '@apollo/client';
import * as Lo from 'lodash';
import {
  EntityParamFormInput,
  gqlAddEntityParam,
  gqlDeleteEntityParam,
  gqlUpdateEntityParam,
} from '../graphql';
import { ENTITY_TYPE_LABEL, EntityTypeValues } from 'shared-pages/common/entity-params-types';

function intLikeToBool(value: any): boolean {
  return value === true || value === 1 || value === '1';
}

function boolLikeToInt(value: any): number {
  if (typeof value === 'boolean') return value ? 1 : 0;
  return intLikeToBool(value) ? 1 : 0;
}

/* ----------- Inline-editable Grid: entity_params ------------------ */
export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    appParamMap,
    initPageIndex,
    onPageChange,
    loading,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    dataCount,
  } = props;

  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const [addEntityParam] = useMutation(gqlAddEntityParam);
  const [updateEntityParam] = useMutation(gqlUpdateEntityParam);
  const [deleteEntityParam] = useMutation(gqlDeleteEntityParam);

  const tableData = useMemo(
    () =>
      (data ?? []).map((row: any) => ({
        ...row,
        // Checkbox editor works reliably with boolean values.
        intValue: intLikeToBool(row?.intValue),
        applies: intLikeToBool(row?.applies),
      })),
    [data],
  );

  const columns = useMemo(
    () => [
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellLeftV8,
        size: 5,
      },

      {
        id: 'paramGroup',
        header: t('Ομάδα Παραμέτρων'),
        columnName: t('Ομάδα Παραμέτρων'),
        accessorKey: 'paramGroup',
        orderByField: 'paramGroup',
        cell: ({ row }: any) => {
          const dataValue = row.original?.paramGroup;
          if (dataValue) return <span>{dataValue}</span>;
          const info = appParamMap?.get(row.original.paramName);
          return <span>{info?.paramGroup ?? '-'}</span>;
        },
        size: 16,
      },
      {
        id: 'paramName',
        header: t('Όνομα Παραμέτρου'),
        columnName: t('Όνομα Παραμέτρου'),
        accessorKey: 'paramName',
        orderByField: 'paramName',
        cell: TableCellV8,
        meta: { type: 'text', required: true },
        size: 24,
      },
      {
        id: 'appParamInfo',
        header: t('Περιγραφή / Σημειώσεις'),
        columnName: t('Περιγραφή / Σημειώσεις'),
        accessorKey: 'paramName',
        cell: ({ row }: any) => {
          const info = appParamMap?.get(row.original.paramName);
          const desc = info?.description ?? '';
          const notes = info?.notes ?? '';
          return (
            <div>
              <div>{desc || <span className="text-muted">-</span>}</div>
              {notes ? <small className="text-muted d-block mt-1">{notes}</small> : null}
            </div>
          );
        },
        size: 40,
      },
      {
        id: 'entityType',
        header: t('Τύπος Παραλήπτη'),
        columnName: t('Τύπος Παραλήπτη'),
        accessorKey: 'entityType',
        orderByField: 'entityType',
        cell: (props: any) => {
          const tableMeta: any = props.table.options.meta;
          const value = props.getValue();
          if (tableMeta?.editedRows[props.row.id]) {
            return <TableCellV8 {...props} />;
          }
          return (
            <div className="text-center">
              <div>{ENTITY_TYPE_LABEL[value] ?? value ?? ''}</div>
              <div style={{ fontFamily: 'monospace', color: 'gray', fontSize: '0.8em' }}>
                {value}
              </div>
            </div>
          );
        },
        meta: { type: 'text', required: true },
        size: 20,
      },
      {
        id: 'applies',
        header: t('Ισχύει'),
        columnName: t('Ισχύει'),
        accessorKey: 'applies',
        orderByField: 'applies',
        cell: TableCellV8,
        meta: {
          onLabel: t('ΝΑΙ'),
          offLabel: t('ΟΧΙ'),
          type: 'checkbox',
          viewCell: i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8,
        },
        size: 10,
      },
      {
        id: 'intValue',
        header: t('Τιμή Παραμέτρου'),
        columnName: t('Τιμή Παραμέτρου'),
        accessorKey: 'intValue',
        orderByField: 'intValue',
        cell: TableCellV8,
        meta: {
          onLabel: t('ΝΑΙ'),
          offLabel: t('ΟΧΙ'),
          type: 'checkbox',
          viewCell: i18n.language === 'gr' ? CellYesNoV8 : CellYesNoEngV8,
        },
        size: 10,
      },

      firstDateUserColumn(t),
      lastDateUserColumn(t),
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 10,
        cell: ({ row, table }: any) => EditCellV8({ row, table }),
      },
    ],
    [t, i18n, appParamMap],
  );

  const tableRef = useRef(null);
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  /* ---- Επιπλέον client-side validation πριν την αποθήκευση -------- */
  function validateValues(values: any): string | null {
    if (!values.paramName || String(values.paramName).trim() === '') {
      return t('Πρέπει να δηλώσετε Όνομα Παραμέτρου');
    }
    if (!values.entityType || String(values.entityType).trim() === '') {
      return t('Πρέπει να δηλώσετε Τύπο Παραλήπτη');
    }
    const allowed: string[] = [
      EntityTypeValues.PlatformUsers,
      EntityTypeValues.Collectors,
      EntityTypeValues.CollectionPoints,
    ];
    if (!allowed.includes(values.entityType)) {
      return t('Μη έγκυρος Τύπος Παραλήπτη. Έγκυρες τιμές: ') + allowed.join(', ');
    }
    return null;
  }

  return (
    <LoadingOverlay active={loading} spinner text={t('Παρακαλώ περιμένετε...')}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="entity-params-grid"
          appStore={appStore}
          tableStore={tableStore as any}
          key="entity-params-grid"
          columns={columns as any}
          data={tableData}
          dataCount={dataCount}
          onRowClick={(table: any) => setSelected && setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style: any = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            return { style: style };
          }}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort: any, columnDef: any, orderBy: string) => {
            onOrderChange && onOrderChange(orderBy);
          }}
          onSaveRow={async (rowIndex: number, row: any, changes: any) => {
            const id = row.id;
            // For edit mode, `changes` may include only touched fields.
            // Merge with existing row so validation always sees required values.
            const values = { ...row, ...changes, id };
            const err = validateValues(values);
            if (err) {
              toastFailure(err);
              return false;
            }
            const mutValues = Lo.pick(values, Lo.keys(new EntityParamFormInput()));
            mutValues.intValue = boolLikeToInt(mutValues.intValue);
            mutValues.applies = intLikeToBool(mutValues.applies);
            // Insert
            if (tableStore.state === TableDataState.insert) {
              const res = await addEntityParam({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('entity-grid addEntityParam error', reason);
                  toastFailure(t('Η εγγραφή απέτυχε'));
                },
              );
              if (!res) return false;
              refetch();
              return res.data.addEntityParam.id;
            }
            // Update
            if (tableStore.state === TableDataState.edit) {
              const res = await updateEntityParam({
                variables: { data: mutValues },
              }).catch((reason) => {
                console.error('entity-grid updateEntityParam error', reason);
                toastFailure(t('Η ενημέρωση απέτυχε'));
              });
              if (!res) return false;
              refetch();
              return id;
            }
            return id;
          }}
          deleteRow={async (row: any) => {
            const ans = await promiseModalDialog({
              title: t('Διαγραφή Εγγραφής'),
              text: `${t('Θέλετε να διαγράψετε την παράμετρο')} <strong>${
                row.paramName
              }</strong> [${row.entityType}];`,
            });
            if (!ans) return;
            const res = await deleteEntityParam({ variables: { id: row.id } }).catch((reason) => {
              console.error('entity-grid deleteEntityParam error', reason);
              toastFailure(t('Η διαγραφή απέτυχε'));
            });
            if (!res) return false;
            refetch();
            toastSuccess(t('Διαγράφηκε η παράμετρος'));
            return true;
          }}
          onNewRow={() => ({
            id: -1,
            paramName: '',
            entityType: EntityTypeValues.PlatformUsers,
            applies: true,
            intValue: true,
          })}
          topBorderRadius={false}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});

DataGrid.displayName = 'EntityParamsDataGrid';
